// Pure helpers for the student page (spec 044 D29–D36): the header, the tabs, the
// child page. No React, no Firestore — jest loads this file, never a page.
import { MONTHS, money, whenLabel } from "./queuePresentation";
import { toDate } from "./ticketPresentation";
import { endOf } from "./lessonPresentation";
import { permissions } from "../config/permissions";

// The list's balance cell too (studentPresentation re-exports it). It lives here so that
// file can import sessionStatusChip (047 D65) without the two importing each other.
export const sessionsLabel = (balance) => {
  const n = Number(balance || 0);
  return `${n} ${n === 1 ? "session" : "sessions"}`;
};

// [year, month, day] from the app's "YYYY-MM-DD" (002: what a child's date_of_birth
// is) or a Firestore Timestamp / Date on an adult's document. A bare string in any
// other shape is null — "03/02/2015" would parse as March in one zone and February
// in another, so it is not guessed.
const ymd = (value) => {
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
  }
  const date = value && typeof value === "object" ? toDate(value) : null;
  return date && !Number.isNaN(date.getTime()) ? [date.getFullYear(), date.getMonth() + 1, date.getDate()] : null;
};

// Age is derived, never stored — the app's src/utils/childUtils.js rule, copied here
// because the dashboard cannot import from the app.
export const ageFrom = (value, today = new Date()) => {
  const parts = ymd(value);
  if (!parts) return null;
  const [y, m, d] = parts;
  let age = today.getFullYear() - y;
  const month = today.getMonth() + 1;
  if (month < m || (month === m && today.getDate() < d)) age -= 1;
  return age >= 0 ? age : null;
};

// "7 years old", "1 year old", or "" — the app's one spelling of an age.
export const ageLabel = (age) =>
  age === null || age === undefined ? "" : `${age} ${age === 1 ? "year" : "years"} old`;

// "born 3 Feb 2015", or "".
export const bornLabel = (value) => {
  const parts = ymd(value);
  if (!parts || parts[1] < 1 || parts[1] > 12) return "";
  const [y, m, d] = parts;
  return `born ${d} ${MONTHS[m - 1]} ${y}`;
};

// The design's word for the fact the list already shows (D36): a parent has children
// on the account, an adult books for themself.
export const typeChip = (user) => ((user?.childCount || 0) > 0 ? "Parent" : "Adult");

export const headerChips = (user) => {
  const chips = [{ label: typeChip(user), color: "default" }];
  if (user?.disabled) chips.push({ label: "Disabled", color: "error" });
  if (user?.oldUser) chips.push({ label: "Legacy", color: "default" });
  if (user?.isInternal) chips.push({ label: "Internal", color: "default" });
  return chips;
};

// The tabs, in the design's order less the ones D30 sent to the backlog. `path` is
// the URL segment after the id; "" is the overview (D29: the URL holds the tab).
export const TABS = [
  { key: "overview", label: "Overview", path: "" },
  { key: "children", label: "Children", path: "children" },
  { key: "sessions", label: "Sessions", path: "sessions" },
  { key: "wallet", label: "Wallet", path: "wallet" },
  { key: "tickets", label: "Tickets", path: "tickets" },
  { key: "notes", label: "Notes", path: "notes" },
];

// The tutor page (047 step 4) passes its own `base` and `tabs`.
export const tabFromPath = (pathname, id, { base = "/students", tabs = TABS } = {}) => {
  const prefix = `${base}/${id}`;
  const path = String(pathname || "");
  if (!path.startsWith(prefix)) return "";
  const rest = path.slice(prefix.length).replace(/^\//, "").split("/")[0];
  return tabs.some((t) => t.path === rest) ? rest : "";
};

// The count pill on a tab (047, "Student page"): Children from `childCount`, Sessions from
// `lessonSummary.count`, both on the users document. null draws no pill.
export const tabCount = (key, student) => {
  if (key === "children") return student?.childCount ?? null;
  if (key === "sessions") return student?.lessonSummary?.count ?? null;
  return null;
};

// The ⋯ menu on the student head (047 D14): 044 D31's account actions, each behind its
// permission (D33). `can(permission)` answers for the viewer's role; `dialog` names the
// dialog the page opens. An empty list hides ⋯. The tutor page's Edit needs EDIT_TUTORS.
export const studentMenuItems = (can, student, editPermission = permissions.EDIT_STUDENTS) =>
  [
    can(editPermission) && { dialog: "edit", label: "Edit" },
    can(permissions.ADJUST_WALLET) && { dialog: "adjust", label: "Adjust balance" },
    can(permissions.BAN_ACCOUNTS) &&
      (student?.disabled
        ? { dialog: "disable", label: "Enable account" }
        : { dialog: "disable", label: "Disable account", danger: true }),
  ].filter(Boolean);

// Sessions tab chips (D35): one status per chip; "All" drops the filter. No Reported chip
// (D37): a report keeps the lesson Finished (012 D3), so no status query finds it.
// Upcoming and Awaiting outcome split Pending at its start (D38), sessionStatusChip's Upcoming
// boundary; a lesson in progress falls under Awaiting outcome and wears its Live now chip (047
// D98). `when` names the side, `startBoundOp` turns it into the query.
export const SESSION_FILTERS = [
  { key: "all", label: "All", status: null, when: null, empty: "No sessions yet." },
  { key: "upcoming", label: "Upcoming", status: "Pending", when: "future", empty: "No upcoming sessions." },
  { key: "awaiting", label: "Awaiting outcome", status: "Pending", when: "past", empty: "No sessions awaiting an outcome." },
  { key: "finished", label: "Completed", status: "Finished", when: null, empty: "No completed sessions." },
  { key: "cancelled", label: "Cancelled", status: "Cancelled", when: null, empty: "No cancelled sessions." },
];

// A zone Intl accepts, else null: the reader's zone, exactly as queuePresentation.parts falls back.
const validZone = (zone) => {
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    return zone;
  } catch (e) {
    if (!(e instanceof RangeError)) throw e;
    return null;
  }
};

// The Sessions tab's subtitle names the zone SessionsTable prints its times in (047 D77): the
// tutor's for every row on the tutor page; on a student's page each booking's `studentTimeZone`,
// else the holder's `timeZone` (also what an empty page says).
export const sessionsZoneNote = (rows, { timeZone, forTutor = false } = {}) => {
  const zones = new Set(
    forTutor || rows.length === 0 ? [validZone(timeZone)] : rows.map((b) => validZone(b.studentTimeZone || timeZone))
  );
  if (zones.size > 1) return "times in each learner's time zone";
  const [zone] = zones;
  return zone ? `times in ${zone} (the ${forTutor ? "tutor" : "learner"}'s time zone)` : "times in your time zone";
};

// The startTimeUTC comparison for a `when` (D38): "future" is after now, "past" is at or
// before it — a lesson starting this instant already reads "Awaiting outcome" on its row.
export const startBoundOp = (when) => (when === "future" ? ">" : when === "past" ? "<=" : null);

// The status chip on a session row. Pending reads "Upcoming" only while the lesson is
// still ahead, and "Live now" between its start and its end (047 D98); a Pending lesson whose
// end has passed is waiting for an outcome, and says so. It can wait for days: lessonLifecycle finishes it only when both parties opened it,
// otherwise it raises a ticket after seven days (012 D1, D8; 044 D38).
// Colours follow Figma's Session status dictionary (047 D15).
export const sessionStatusChip = (booking, now = new Date()) => {
  const status = booking?.status;
  // A report is a mark on a finished lesson (012 D3); the old client wrote status "Reported".
  // The same rule as the Lessons page's Reported tab (lessonTabs.js matchesTab).
  if (booking?.reportedBy || booking?.reportedAt || status === "Reported") return { label: "Reported", color: "warning" };
  if (status === "Finished") return { label: "Completed", color: "success" };
  if (status === "Cancelled") return { label: "Cancelled", color: "default" };
  if (status === "Pending") {
    const start = toDate(booking.startTimeUTC);
    if (start && start > now) return { label: "Upcoming", color: "info" };
    const end = endOf(booking);
    return end && end > now ? { label: "Live now", color: "info" } : { label: "Awaiting outcome", color: "warning" };
  }
  return { label: status || "Unknown", color: "default" };
};

// "Self" for the account holder, the child's name when the subcollection has it,
// "Child" when it does not (a deleted child, 002).
export const learnerName = (booking, childrenById = {}) => {
  const learner = booking?.learner;
  if (!learner || learner.kind !== "child") return "Self";
  const child = childrenById[learner.childId];
  return (child && child.name) || "Child";
};

export const durationLabel = (minutes) => (minutes ? `${minutes} min` : "-");

// A person's name from their users document, with Lessons.js's fallback chain.
export const personName = (user) =>
  (user && (user.name || user.fullName || user.displayName || user.email)) || "";

const TYPE_LABELS = {
  lesson: "Lesson",
  trial: "Free session",
  adminadjustment: "Adjustment",
  referralreward: "Referral reward",
  cancelledwithoutrefund: "Cancelled, no refund",
};

// One ledger row as the Wallet tab shows it. The sign comes from the balances when the
// writer recorded them (adjustWallet, referral); otherwise from `type`: debit and
// payout take, refund gives — except the legacy cash rows (they carry `currency`),
// where a refund is money leaving, the rule the old drawer kept.
export const txnRow = (t = {}) => {
  const type = String(t.type || "").toLowerCase();
  const kind = String(t.transactionType || "").toLowerCase();
  // A top-up (functions/index.js) is two documents: the "created" placeholder written
  // when checkout starts, which the webhook never touches, and the webhook's own row
  // under the PaymentIntent id, carrying `hours`. Only the second moved the balance.
  if (kind === "topup") {
    const when = toDate(t.createdAt);
    const paid = t.amount ? `${money(t.amount)} ${String(t.currency || "").toUpperCase()}`.trim() : "";
    if (t.status && t.status !== "succeeded") {
      return { when, type: "Checkout started", description: paid, amountLabel: "—", negative: false, balanceAfter: "—" };
    }
    const hours = Number(t.hours) || 0;
    return { when, type: "Top-up", description: paid ? `${paid} paid` : "", amountLabel: hours ? `+${sessionsLabel(hours)}` : paid ? `+${paid}` : "—", negative: false, balanceAfter: "—" };
  }
  const hasBalances = typeof t.balanceBefore === "number" && typeof t.balanceAfter === "number";
  const amount = Number(t.amount) || 0;
  const negative =
    amount > 0 &&
    (hasBalances
      ? t.balanceAfter < t.balanceBefore
      : type === "debit" || type === "payout" || (type === "refund" && !!t.currency));
  const unit = t.currency ? String(t.currency).toUpperCase() : "";
  // A cash amount prints at most two decimals (047 D79); sessions are whole or halves already.
  const size = (n) => (unit ? `${money(n)} ${unit}` : sessionsLabel(n));
  return {
    when: toDate(t.createdAt),
    type:
      TYPE_LABELS[kind] ||
      (type === "refund" ? "Refund" : type === "debit" ? "Lesson" : type === "payout" ? "Payout" : "Transaction"),
    description: t.reason || (t.bookingId ? `Lesson ${String(t.bookingId).slice(0, 8)}` : ""),
    amountLabel: amount === 0 ? size(0) : `${negative ? "−" : "+"}${size(amount)}`,
    negative,
    balanceAfter: typeof t.balanceAfter === "number" ? sessionsLabel(t.balanceAfter) : "—",
  };
};

// Two queries, one list (D35): a ticket the person raised and a system ticket about
// their lesson can be the same document; newest activity first, capped.
export const mergeTickets = (raised = [], about = [], cap = 50) => {
  const byId = new Map();
  [...raised, ...about].forEach((t) => {
    if (t && t.id && !byId.has(t.id)) byId.set(t.id, t);
  });
  const at = (t) => toDate(t.lastActivityAt || t.raisedAt)?.getTime() || 0;
  return [...byId.values()].sort((a, b) => at(b) - at(a)).slice(0, cap);
};

// "Quran · Maryam · Sun 30 Aug, 18:00" from the lesson snapshot a report or a system
// ticket carries; "—" for a plain request.
export const ticketLessonLabel = (ticket, { timeZone } = {}) => {
  if (!ticket) return "—";
  const parts = [
    ticket.subject,
    ticket.learnerName,
    ticket.startTimeUTC ? whenLabel(ticket.startTimeUTC, { timeZone }) : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
};

// The Overview's count cards (D35): the two the user document carries, three counts (Pending
// split at now, D38); no Reported card (D37). A count that has not landed (or failed) is
// null/undefined and renders "—".
export const overviewCards = (summary = {}, counts = {}) => [
  { key: "total", label: "Total sessions", value: summary.count ?? 0 },
  { key: "finished", label: "Completed", value: summary.finished ?? 0 },
  { key: "upcoming", label: "Upcoming", value: counts.upcoming },
  { key: "awaiting", label: "Awaiting outcome", value: counts.awaiting },
  { key: "cancelled", label: "Cancelled", value: counts.cancelled },
];

export const childCards = (counts = {}) => [
  { key: "taken", label: "Sessions", value: counts.taken },
  { key: "finished", label: "Completed", value: counts.finished },
  { key: "cancelled", label: "Cancelled", value: counts.cancelled },
];

export const countLabel = (value) => (value === null || value === undefined ? "—" : String(value));

// Oldest first, a child with no createdAt last — so "child 2 of 3" is stable.
export const sortChildren = (children = []) =>
  [...children].sort((a, b) => {
    const at = (c) => toDate(c.createdAt)?.getTime();
    const x = at(a);
    const y = at(b);
    if (x === undefined && y === undefined) return 0;
    if (x === undefined) return 1;
    if (y === undefined) return -1;
    return x - y;
  });

export const childPosition = (children = [], childId) => {
  const i = children.findIndex((c) => c.id === childId);
  return i < 0 ? "" : `child ${i + 1} of ${children.length}`;
};
